import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { SortUtil } from '@iot-platform/iot-platform-utils';
import { FavoriteFilter, FavoriteFiltersByConcept, UserPreferences } from '@iot-platform/models/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isFieldOfTypeList } from '../../helpers';
import { FilterCategoryV2, FilterFieldV2 } from '../../models';

interface ManageFavoriteFiltersDialogData {
  categories: FilterCategoryV2[];
  masterView: string;
  userPreferences: UserPreferences | undefined;
}

interface ManageFavoriteFiltersForm {
  favoriteFilters: FormControl<FavoriteFiltersByConcept>;
}

@Component({
  imports: [
    FlexLayoutModule,
    TranslatePipe,
    MatCardModule,
    MatToolbarModule,
    MatIcon,
    MatButtonModule,
    ReactiveFormsModule,
    MatCheckbox,
    MatChipsModule,
    DragDropModule
  ],
  selector: 'iot-platform-ui-manage-favorite-filters-popup-v2',
  templateUrl: './manage-favorite-filters-popup-v2.component.html',
  styleUrls: ['./manage-favorite-filters-popup-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class ManageFavoriteFiltersPopupV2Component implements OnInit, OnDestroy {
  readonly data = inject<ManageFavoriteFiltersDialogData>(MAT_DIALOG_DATA);

  readonly FAVORITE_FILTERS_MAXIMUM = 8;

  readonly favoriteFiltersMaximumPossible = signal<number>(0);
  readonly favoriteFiltersTotal = signal<number>(0);
  readonly favoriteFiltersForOrdering = signal<FavoriteFilter[]>([]);

  readonly manageFavoriteFiltersForm = new FormGroup<ManageFavoriteFiltersForm>({
    favoriteFilters: new FormControl<FavoriteFiltersByConcept>(
      {},
      {
        validators: [Validators.required, this.minFavoriteFilterValidator()],
        nonNullable: true
      }
    )
  });
  readonly favoriteFiltersControl = this.manageFavoriteFiltersForm.controls.favoriteFilters;
  private readonly dialogRef = inject(MatDialogRef<ManageFavoriteFiltersPopupV2Component>);
  private readonly destroy$ = new Subject<void>();

  getIsFieldOfTypeList(field: FavoriteFilter): boolean {
    return isFieldOfTypeList(field as unknown as FilterFieldV2);
  }

  ngOnInit(): void {
    this.setFavoriteFiltersMaximumPossible();
    this.initForm();

    this.calculateFavoriteFiltersCount(this.favoriteFiltersControl.getRawValue());

    this.favoriteFiltersControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((filters) => {
      this.calculateFavoriteFiltersCount(filters);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isFavoriteFilter(field: FilterFieldV2, categoryKey: string): boolean {
    const favoriteFilters = this.favoriteFiltersControl.getRawValue();
    return !!favoriteFilters[categoryKey]?.find((f) => f.name === field.key);
  }

  /**
   * Determine if a field checkbox should be disabled
   * A field is disabled only when it's NOT selected AND we're at maximum capacity
   */
  isFieldDisabled(field: FilterFieldV2, categoryKey: string): boolean {
    const isCurrentlyFavorite = this.isFavoriteFilter(field, categoryKey);
    const atMaximumCapacity = this.favoriteFiltersTotal() === this.favoriteFiltersMaximumPossible();

    // Only disable if not selected and at maximum capacity
    return !isCurrentlyFavorite && atMaximumCapacity;
  }

  onFilterSelectionChange(event: MatCheckboxChange, category: FilterCategoryV2, field: FilterFieldV2): void {
    this.manageFavoriteFiltersForm.markAsDirty();

    const categoryKey = category.key;
    const fieldKey = field.key;
    const favoriteFilters: FavoriteFiltersByConcept = { ...this.favoriteFiltersControl.getRawValue() };

    if (event.checked) {
      const currentOrdering = this.favoriteFiltersForOrdering();
      const newOrder = currentOrdering.length;

      if (!favoriteFilters[categoryKey]) {
        favoriteFilters[categoryKey] = [];
      }
      favoriteFilters[categoryKey].push({ name: fieldKey, label: field.fullLabel, fieldType: field.fieldType, order: newOrder });
    } else {
      const filterIndex = favoriteFilters[categoryKey]?.findIndex((filter) => filter.name === fieldKey);
      if (filterIndex !== undefined && filterIndex !== -1) {
        favoriteFilters[categoryKey].splice(filterIndex, 1);
      }
    }

    const cleanedFilters = this.cleanFavoriteFilters(favoriteFilters);
    this.favoriteFiltersControl.setValue(cleanedFilters);
    this.updateFavoriteFiltersForOrdering();
  }

  resetToAppDefault(): void {
    const defaultFilters = this.getAppDefaultFavoriteFilters();
    this.favoriteFiltersControl.setValue(defaultFilters);
    this.sortAndOrderFavoriteFilters();
    this.updateFavoriteFiltersForOrdering();
    this.setFavoriteFiltersNewOrders();
    this.manageFavoriteFiltersForm.markAsDirty();
  }

  drop(event: CdkDragDrop<FavoriteFilter[]>): void {
    const currentOrdering = [...this.favoriteFiltersForOrdering()];
    moveItemInArray(currentOrdering, event.previousIndex, event.currentIndex);

    const reorderedFilters = currentOrdering.map((filter, index) => ({
      ...filter,
      order: index
    }));

    this.favoriteFiltersForOrdering.set(reorderedFilters);
    this.setFavoriteFiltersNewOrders();
    this.manageFavoriteFiltersForm.markAsDirty();
  }

  save(): void {
    if (this.manageFavoriteFiltersForm.valid) {
      this.dialogRef.close(this.favoriteFiltersControl.getRawValue());
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  private minFavoriteFilterValidator(): ValidatorFn {
    return (control): ValidationErrors | null => {
      const value = control.value as FavoriteFiltersByConcept;
      const keys = Object.keys(value);

      return !keys.find((key) => !!value[key]?.length) ? { favoriteFiltersEmpty: true } : null;
    };
  }

  private initForm(): void {
    const userPreferences = this.data.userPreferences;
    const masterView = this.data.masterView;

    if (userPreferences?.favoriteFilters?.[masterView]) {
      const cleanedFilters = this.removeDeprecatedFilters(userPreferences.favoriteFilters[masterView]);
      this.favoriteFiltersControl.setValue(cleanedFilters);
    } else {
      const defaultFilters = this.getAppDefaultFavoriteFilters();
      this.favoriteFiltersControl.setValue(defaultFilters);
      this.sortAndOrderFavoriteFilters();
    }

    this.updateFavoriteFiltersForOrdering();
    this.setFavoriteFiltersNewOrders();
  }

  private removeDeprecatedFilters(favoriteFilters: FavoriteFiltersByConcept): FavoriteFiltersByConcept {
    const categories = this.data.categories;
    const cleanFilters: FavoriteFiltersByConcept = {};

    Object.entries(favoriteFilters).forEach(([categoryKey, categoryFilters]) => {
      categoryFilters.forEach((ff) => {
        let matchingField: FilterFieldV2 | undefined;
        let actualCategoryKey = categoryKey;

        // First, try to find it in the original category
        const originalCategory = categories.find((cat) => cat.key === categoryKey);
        if (originalCategory) {
          matchingField = originalCategory.fields.find((field) => field.key === ff.name);
        }

        // If not found in original category, search all categories (handles moved filters)
        if (!matchingField) {
          for (const category of categories) {
            matchingField = category.fields.find((field) => field.key === ff.name);
            if (matchingField) {
              actualCategoryKey = category.key; // Update to new category
              break;
            }
          }
        }

        // Add to cleaned filters if found
        if (matchingField) {
          if (!cleanFilters[actualCategoryKey]) {
            cleanFilters[actualCategoryKey] = [];
          }
          cleanFilters[actualCategoryKey].push({
            ...ff,
            label: matchingField.fullLabel,
            fieldType: matchingField.fieldType
          });
        }
        // If not found in any category, it's truly deprecated - skip it
      });
    });

    return cleanFilters;
  }

  private getAppDefaultFavoriteFilters(): FavoriteFiltersByConcept {
    const categories = this.data.categories;
    const defaultFilters: FavoriteFiltersByConcept = {};

    categories.forEach((category) => {
      const defaultFavoriteFields = category.fields
        .filter((field) => field.defaultFavorite)
        .map((field, index) => ({ name: field.key, label: field.fullLabel, fieldType: field.fieldType, order: index }));

      if (defaultFavoriteFields.length > 0) {
        defaultFilters[category.key] = defaultFavoriteFields;
      }
    });

    return defaultFilters;
  }

  private setFavoriteFiltersMaximumPossible(): void {
    const categories = this.data.categories;
    const filtersTotal = categories.reduce((acc, category) => acc + category.fields.length, 0);
    this.favoriteFiltersMaximumPossible.set(filtersTotal < this.FAVORITE_FILTERS_MAXIMUM ? filtersTotal : this.FAVORITE_FILTERS_MAXIMUM);
  }

  /**
   * Calculates and updates the total count of favorite filters across all categories.
   * Handles all edge cases including null, undefined, invalid types, empty objects, and non-array values.
   *
   * @param favoriteFilters - Object containing favorite filters grouped by category key
   * @private
   */
  private calculateFavoriteFiltersCount(favoriteFilters: FavoriteFiltersByConcept): void {
    if (!favoriteFilters) {
      this.favoriteFiltersTotal.set(0);
      return;
    }

    // Calculate total from all categories, handling all edge cases
    const total = Object.values(favoriteFilters).reduce((accumulator, filters) => {
      // Only count if filters is a valid non-empty array
      if (Array.isArray(filters) && filters.length > 0) {
        return accumulator + filters.length;
      }
      return accumulator;
    }, 0);

    this.favoriteFiltersTotal.set(total);
  }

  private cleanFavoriteFilters(favoriteFilters: FavoriteFiltersByConcept): FavoriteFiltersByConcept {
    const cleanedFavoriteFilters: FavoriteFiltersByConcept = {};

    Object.keys(favoriteFilters).forEach((key) => {
      if (favoriteFilters[key]?.length > 0) {
        cleanedFavoriteFilters[key] = [...favoriteFilters[key]];
      }
    });

    return cleanedFavoriteFilters;
  }

  private sortAndOrderFavoriteFilters(): void {
    const ordering = this.favoriteFiltersForOrdering();
    const sortedFilters = [...ordering].sort(SortUtil.sortByName);
    const orderedFilters = sortedFilters.map((filter, index) => ({
      ...filter,
      order: index
    }));
    this.favoriteFiltersForOrdering.set(orderedFilters);
  }

  private updateFavoriteFiltersForOrdering(): void {
    const favoriteFilters = this.favoriteFiltersControl.getRawValue();
    const flatFilters = Object.values(favoriteFilters).flat().sort(SortUtil.sortByOrder);
    this.favoriteFiltersForOrdering.set(flatFilters);
  }

  private setFavoriteFiltersNewOrders(): void {
    const newFavoriteFiltersByConcept: FavoriteFiltersByConcept = {
      ...this.favoriteFiltersControl.getRawValue()
    };
    const ordering = this.favoriteFiltersForOrdering();

    Object.keys(newFavoriteFiltersByConcept).forEach((categoryKey) => {
      newFavoriteFiltersByConcept[categoryKey] = newFavoriteFiltersByConcept[categoryKey].map((filterToUpdate) => {
        const matchingFilter = ordering.find((filterWithNewOrder) => filterWithNewOrder.name === filterToUpdate.name);
        if (matchingFilter) {
          return { ...filterToUpdate, order: matchingFilter.order };
        }
        return filterToUpdate;
      });
    });

    this.favoriteFiltersControl.setValue(newFavoriteFiltersByConcept);
  }
}
